import React, { useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchMetalGateReferenceImage } from '../../services/project.service';

const SIZE = 36;

const placeholderSx = {
	width: SIZE,
	height: SIZE,
	mr: 1,
	flexShrink: 0,
	borderRadius: 1,
	border: '1px dashed #cbd5e1',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	textAlign: 'center',
	fontSize: 9,
	lineHeight: 1.1,
	color: '#94a3b8',
} as const;

/** Reference photo a Metal Gate sub type was created from; click to enlarge. */
export const MetalGateReferenceThumbnail: React.FC<{
	projectToSubTypeId?: number | null;
	hasReferenceImage?: boolean;
	label: string;
}> = ({ projectToSubTypeId, hasReferenceImage, label }) => {
	const [url, setUrl] = useState<string | null>(null);
	const [failed, setFailed] = useState(false);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!hasReferenceImage || !projectToSubTypeId) return;
		let active = true;
		let objectUrl: string | null = null;
		setFailed(false);
		fetchMetalGateReferenceImage(projectToSubTypeId)
			.then((blob) => {
				if (!active) return;
				objectUrl = URL.createObjectURL(blob);
				setUrl(objectUrl);
			})
			.catch(() => active && setFailed(true));
		return () => {
			active = false;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [projectToSubTypeId, hasReferenceImage]);

	if (!hasReferenceImage || failed) {
		return (
			<Tooltip title={failed ? 'Reference photo could not be loaded' : 'No reference photo stored'}>
				<Box sx={placeholderSx}>No photo</Box>
			</Tooltip>
		);
	}
	if (!url) return <Box sx={{ ...placeholderSx, border: '1px solid #e2e8f0' }} />;

	return (
		<>
			<Tooltip title='View reference photo'>
				<Box
					component='img'
					src={url}
					alt={`Reference photo for ${label}`}
					onClick={(event: React.MouseEvent) => {
						event.stopPropagation();
						setOpen(true);
					}}
					sx={{
						width: SIZE,
						height: SIZE,
						mr: 1,
						flexShrink: 0,
						objectFit: 'cover',
						borderRadius: 1,
						border: '1px solid #e2e8f0',
						cursor: 'zoom-in',
					}}
				/>
			</Tooltip>
			<Dialog open={open} onClose={() => setOpen(false)} maxWidth='md' onClick={(event) => event.stopPropagation()}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 2, pr: 1, pt: 1 }}>
					<Typography variant='subtitle2'>Reference photo · {label}</Typography>
					<IconButton onClick={() => setOpen(false)} aria-label='Close reference photo'>
						<CloseIcon />
					</IconButton>
				</Box>
				<DialogContent sx={{ pt: 0 }}>
					<Box
						component='img'
						src={url}
						alt={`Reference photo for ${label}`}
						sx={{ display: 'block', maxWidth: '100%', maxHeight: '75vh', mx: 'auto' }}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
};
